import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import { Fragment } from "react/jsx-runtime";
import { useToast, Icon, Icons, Division, Heading, Span, Button, Chip } from "@ir/ui";
import { useWorkspace } from "@/hooks/workspace";
import { useElementHeight } from "@/hooks/measurements";
import { HasAnyPermission } from "@/presentation/workspace";
import useInternalConversation from "@/presentation/deduction/internal-conversations/hooks/useInternalConversation";
import MessageItem from "@/presentation/deduction/internal-conversations/InternalConversationMessage";
import InternalConversationLoading from "@/presentation/deduction/internal-conversations/InternalConversationLoading";
import {
  ConversationUtils,
  GroupedMessages,
} from "@/presentation/deduction/internal-conversations/utils/ConversationUtils";
import InternalConversationEmpty from "@/presentation/deduction/internal-conversations/InternalConversationEmpty";
import InternalConversationPostMessage from "@/presentation/deduction/internal-conversations/InternalConversationPostMessage";
import InternalConversationLoadMore from "@/presentation/deduction/internal-conversations/InternalConversationLoadMore";
import { WorkspacePermissions } from "@/core/permissions/Workspace.permissions";
import { ECurrentMessageReceiveAction } from "./interface/InternalConversationContextType";
import "./InternalConversation.scss";
import { CategoryKey } from "@/core/entities/deduction";
import { useDeductionDrawer } from "../hooks/drawer/useDeductionDrawer";
import { useEventBusSubscriber } from "@/hooks";
import { NotificationEvents } from "@/core/events/notificationEventsMap";

export interface IContainerScrollInfo {
  scrollTop: number;
  scrollHeight: number;
  isScrolledToBottom: boolean;
  isScrolledToTop: boolean;
}

export interface IInternalConversationProps {
  referenceId: number;
  categoryKey: string;
}

/**
 ********************* Internal Conversation Component *********************
 */
const InternalConversation: React.FC<IInternalConversationProps> = ({ referenceId, categoryKey }) => {
  const {
    conversation,
    groupedMessages,
    currentMessageReceiveAction,
    setCurrentMessageReceiveAction,
    lastReceivedMessage,
    isInitialLoading,
    isLoadingMore,
    hasMore,
    isError,
    fetchConversation,
    fetchMessages,
    sendMessage,
    receiveNewConversationMessage,
    uploadConversationAttachment,
  } = useInternalConversation();

  const [allGroupedMessages, setAllGroupedMessages] = useState<GroupedMessages[]>([]);
  const [messageMainSectionRef, messageMainSectionHeight] = useElementHeight<HTMLDivElement>();
  const [postMessageContainerRef, postMessageContainerHeight] = useElementHeight<HTMLDivElement>();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [newMessageCountForScroll, setNewMessageCountForScroll] = useState(0);
  const { error: errorToast } = useToast();
  const { settings } = useWorkspace();
  const { deductionsCount, drawerInfo } = useDeductionDrawer();

  /**
   ********************* Height Calculation for the Message Container *********************
   */
  const mainContainerHeight = useMemo(() => {
    return messageMainSectionHeight - postMessageContainerHeight;
  }, [messageMainSectionHeight, postMessageContainerHeight]);

  /**
   ********************* Show Error Toast *********************
   */
  useEffect(() => {
    if (isError) {
      errorToast({
        content: "An error occurred while fetching conversation messages",
        icon: <Icon name={Icons.AlertCircle} />,
        duration: 5000,
      });
    }
  }, [isError, errorToast]);

  /**
   ********************* Fetch Conversation by Remittance Item ID *********************
   */
  useEffect(() => {
    if (!referenceId || !categoryKey) return;
    fetchConversation(referenceId, categoryKey as CategoryKey);
  }, [referenceId, categoryKey, deductionsCount, fetchConversation]);

  /**
   ********************* Fetch Messages by Conversation ID *********************
   */
  useEffect(() => {
    if (!conversation?.id) return;
    fetchMessages();
  }, [conversation?.id, deductionsCount, fetchMessages]);

  const [previousScrollHeight, setPreviousScrollHeight] = useState(0);
  const [previousScrollTop, setPreviousScrollTop] = useState(0);
  const [previousClientHeight, setPreviousClientHeight] = useState(0);
  const [previousScrollContainer, setPreviousScrollContainer] = useState<HTMLDivElement | null>(null);

  /**
   ********************* Handle Scroll *********************
   */
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isScrolledToTop = scrollTop <= 10;

    if (isScrolledToTop && hasMore && !isLoadingMore && !isInitialLoading) {
      fetchMessages();
    }
    setPreviousScrollHeight(messagesContainerRef.current.scrollHeight);
    setPreviousScrollTop(messagesContainerRef.current.scrollTop);
    setPreviousClientHeight(messagesContainerRef.current.clientHeight);
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      setNewMessageCountForScroll(0);
    }
  }, [hasMore, isLoadingMore, isInitialLoading, fetchMessages]);

  /**
   ********************* Add Event Listener to Scroll *********************
   */
  useEffect(() => {
    const container = messagesContainerRef.current;

    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  /**
   ********************* Set Previous Scroll Information and Grouped Messages *********************
   */
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      setPreviousScrollHeight(container.scrollHeight);
      setPreviousScrollTop(container.scrollTop);
      setPreviousClientHeight(container.clientHeight);
      setPreviousScrollContainer(container);
    }

    if (groupedMessages.length > 0) {
      setAllGroupedMessages(groupedMessages);
    } else {
      setAllGroupedMessages([]);
    }
  }, [groupedMessages, messagesContainerRef]);

  /**
   ********************* Set scroll position based on the current message receive action *********************
   */
  useEffect(() => {
    if (
      currentMessageReceiveAction &&
      previousScrollContainer &&
      previousScrollContainer.scrollHeight > 0 &&
      messagesContainerRef.current
    ) {
      const _messageReceiveAction = currentMessageReceiveAction;

      if (_messageReceiveAction === ECurrentMessageReceiveAction.INITIAL_LOAD) {
        messagesContainerRef.current.scrollTop = previousScrollContainer.scrollHeight;
      } else if (_messageReceiveAction === ECurrentMessageReceiveAction.LOAD_MORE) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight - previousScrollHeight;
      } else if (
        _messageReceiveAction === ECurrentMessageReceiveAction.RECEIVE_NEW_MESSAGE ||
        _messageReceiveAction === ECurrentMessageReceiveAction.SEND_MESSAGE
      ) {
        if (previousScrollTop + previousClientHeight >= previousScrollHeight - 1 || lastReceivedMessage?.isOwner) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight + 100;
        } else {
          setTimeout(() => {
            setNewMessageCountForScroll(prev => prev + 1);
          }, 100);
        }
      }
      setTimeout(() => {
        setPreviousScrollContainer(null);
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentMessageReceiveAction,
    allGroupedMessages,
    lastReceivedMessage,
    // Don't need any other dependencies for this useEffect
  ]);

  const handleMessageSent = useCallback(() => {
    setCurrentMessageReceiveAction(ECurrentMessageReceiveAction.SEND_MESSAGE);
  }, [setCurrentMessageReceiveAction]);

  /**
   ********************* Subscribe to real-time conversation messages *********************
   */
  useEventBusSubscriber(NotificationEvents.NEW_CONVERSATION_MESSAGE, payload => {
    if (!conversation || conversation.id !== payload.conversationId) return;

    receiveNewConversationMessage(payload);

    setTimeout(() => {
      setCurrentMessageReceiveAction(ECurrentMessageReceiveAction.RECEIVE_NEW_MESSAGE);
    }, 10);
  });

  const isSingleDeduction = useMemo(
    () => drawerInfo?.remittanceItem.deductionCount === 1 || drawerInfo?.isDeductionIncomplete,
    [drawerInfo]
  );

  const containerHeight = useMemo(() => {
    const baseOffset = isSingleDeduction ? 160 : 165;
    const accordionHeaderHeight = 45;
    const count = deductionsCount > 1 ? deductionsCount : 0;
    return `calc(100vh - ${baseOffset + count * accordionHeaderHeight}px)`;
  }, [isSingleDeduction, deductionsCount]);

  /**
   ********************* Render the Internal Conversation Component *********************
   */

  return (
    <section className="internal-conversation" ref={messageMainSectionRef} style={{ height: containerHeight }}>
      <Division className="internal-conversation-main" style={{ height: `${mainContainerHeight}px` }}>
        <main className="internal-conversation-messages" ref={messagesContainerRef}>
          {isInitialLoading && <InternalConversationLoading />}
          {isLoadingMore && <InternalConversationLoadMore />}
          {!isInitialLoading && (
            <Division>
              {groupedMessages.length === 0 ? (
                <InternalConversationEmpty />
              ) : (
                allGroupedMessages.map(group => (
                  <Fragment key={group.date}>
                    <Division key={group.date}>
                      <Division className="internal-conversation-messages-formatted-date ">
                        <Heading level={3} className="text-center text-sm text-gray-500">
                          <Span>{ConversationUtils.formatDate(group.date, settings?.displayDateFormat)}</Span>
                        </Heading>
                      </Division>
                      {group.messages.map(message => (
                        <MessageItem key={message.requestUniqueKey} message={message} />
                      ))}
                    </Division>
                  </Fragment>
                ))
              )}
            </Division>
          )}
        </main>
        {newMessageCountForScroll > 0 && (
          <Button
            noStyle
            size="small"
            className="view-new-messages-button"
            onPress={() => {
              if (messagesContainerRef.current) {
                messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
              }
              setNewMessageCountForScroll(0);
            }}
          >
            <Chip
              label={`${newMessageCountForScroll} new message(s)`}
              size="large"
              icon={<Icon name={Icons.ArrowNarrowDown} />}
              iconPosition="right"
              aria-label={`${newMessageCountForScroll} new message(s)`}
              style={{ backgroundColor: "#fff" }}
            />
          </Button>
        )}
      </Division>
      <HasAnyPermission requiredPermissions={[WorkspacePermissions.CONVERSATIONS_ADD]}>
        <Division className="internal-conversation-post-message" ref={postMessageContainerRef}>
          <InternalConversationPostMessage
            setCurrentMessageReceiveAction={setCurrentMessageReceiveAction}
            onMessageSent={handleMessageSent}
            sendMessage={sendMessage}
            uploadConversationAttachment={uploadConversationAttachment}
            conversation={conversation}
          />
        </Division>
      </HasAnyPermission>
    </section>
  );
};

export default InternalConversation;
